// @flow

import type { WidgetData } from '@auravisionlabs/aura-pkg-report-widgets';
import {
  renderReportAsMarkdown,
  buildWidgetDateString,
  sortWidgetData,
} from '@auravisionlabs/aura-pkg-report-widgets';
import type {
  ProcessedReport,
  ProcessedSection,
  ProcessedWidget,
} from '@auravisionlabs/aura-pkg-report-widgets';

export type WidgetState = {
  id: string,
  status: 'registered' | 'loading' | 'complete' | 'error',
  data: WidgetData | null,
  error: string | null,
  inputsSignature?: string | null,
};

type ReportSpec = {
  title?: string,
  description?: string,
  period?: any,
  comparisonPeriod?: any,
  sections: Array<{
    sectionTitle?: string,
    period?: any,
    comparisonPeriod?: any,
    widgets: Array<any>,
  }>,
};

type Location = {
  id: string,
  name: string,
};

/**
 * Generate period string using package utility
 */
function generatePeriodString(config: {
  period?: any,
  comparisonPeriod?: any,
  showChangeValue?: boolean,
}): string {
  if (!config.period?.selectedDates) {
    return '';
  }
  return buildWidgetDateString({
    period: config.period,
    comparisonPeriod: config.comparisonPeriod,
    showChangeValue: config.showChangeValue ?? true,
  });
}

/**
 * Transform the report and collected widget data into markdown.
 * Uses package's renderReportAsMarkdown for consistent output.
 */
export function transformReportToMarkdown(
  reportSpec: ReportSpec,
  widgetRegistry: Map<string, WidgetState>,
  locations: Location[],
  startOfWeek: 'monday' | 'sunday',
  activeInputsSignature?: string
): string {
  // Build ProcessedReport structure that matches package expectations
  
  // Report-level period string
  const reportPeriodString = generatePeriodString({
    period: reportSpec.period,
    comparisonPeriod: reportSpec.comparisonPeriod,
  });

  // Build processed sections
  const processedSections: ProcessedSection[] = [];

  if (reportSpec.sections) {
    reportSpec.sections.forEach((section, sectionIndex) => {
      // Section-level period (inherits from report if not set)
      const sectionPeriod = section.period?.selectedDates
        ? section.period
        : reportSpec.period;
      const sectionCompPeriod = section.comparisonPeriod?.selectedDates
        ? section.comparisonPeriod
        : reportSpec.comparisonPeriod;

      const sectionPeriodString = generatePeriodString({
        period: sectionPeriod,
        comparisonPeriod: sectionCompPeriod,
      });

      // Build processed widgets
      const processedWidgets: ProcessedWidget[] = [];

      if (section.widgets) {
        section.widgets.forEach((widget, widgetIndex) => {
          const widgetId = `s${sectionIndex}-w${widgetIndex}`;
          const widgetState = widgetRegistry.get(widgetId);

          const isFreshForInputs =
            !activeInputsSignature ||
            (widgetState && widgetState.inputsSignature === activeInputsSignature);

          if (widgetState && widgetState.data && isFreshForInputs) {
            // Widget config may be nested under widgetConfig (webclient structure)
            const widgetConfig = widget.widgetConfig || widget;
            
            // Widget-level period (inherits from section, then report)
            const widgetPeriod = widgetConfig.period?.selectedDates
              ? widgetConfig.period
              : sectionPeriod;
            const widgetCompPeriod = widgetConfig.comparisonPeriod?.selectedDates
              ? widgetConfig.comparisonPeriod
              : sectionCompPeriod;

            const widgetPeriodString = generatePeriodString({
              period: widgetPeriod,
              comparisonPeriod: widgetCompPeriod,
              showChangeValue: widgetConfig.showChangeValue,
            });

            // Apply sorting for rank and metricHeatmap widgets (matches headless behavior)
            const widgetType = widget.widgetType || 'unknown';
            const sortBy = widgetConfig.sortBy;
            let widgetData = widgetState.data;
            
            if ((widgetType === 'rank' || widgetType === 'metricHeatmap') && sortBy) {
              widgetData = sortWidgetData(widgetData, sortBy);
            }

            processedWidgets.push({
              id: widgetId,
              widgetType,
              widgetTitle: widgetData.title || '',
              periodString: widgetPeriodString,
              data: widgetData,
              aggregation: widgetConfig.aggregation,
              locations: widgetConfig.locations,
            });
          }
        });
      }

      processedSections.push({
        sectionTitle: section.sectionTitle || '',
        periodString: sectionPeriodString,
        widgets: processedWidgets,
      });
    });
  }

  // Build the full ProcessedReport
  const locationCount = locations.length;
  const processedReport: ProcessedReport = {
    reportTitle: reportSpec.title || '',
    reportDescription: reportSpec.description,
    locationMetadata: `Locations: ${locationCount} location${locationCount !== 1 ? 's' : ''} included in this report`,
    periodString: reportPeriodString,
    sections: processedSections,
  };

  // Use package's renderReportAsMarkdown
  return renderReportAsMarkdown(processedReport, {}, { locations, startOfWeek });
}

/**
 * Create error widget data structure.
 */
export function createErrorWidgetData(
  widgetId: string,
  error: string
): WidgetData {
  return {
    title: `Widget ${widgetId}`,
    type: 'table',
    columns: [{ header: 'Status', key: 'status', defaultUnit: 'string' }],
    rows: [],
    error,
  };
}

/**
 * Generate a hash signature for the markdown content.
 * Uses a simple hash for change detection.
 */
export function generateSignature(content: string): string {
  // Simple hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < content.length; i++) {
    hash = ((hash << 5) + hash) + content.charCodeAt(i);
  }
  return hash.toString(36);
}
